import React from 'react';
import { Separator } from './ui/separator';
import { Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath, SEO_PAGE_KEYS } from '../i18n/routes';
import { LEGAL_NOTICE_LINKS } from '../i18n/legalNotice';

const serviceLinks = [
  'MSN Microsoft Network',
  'Coupon & Deals',
  'Commerce Content',
  'Buy Now Pay Later (BNPL)',
  'Browser Autocomplete',
  'Deep Link Technology'
];

const SEO_LINK_LABELS = /* i18n:extract */ {
  bestNetwork: "Meilleur réseau d'affiliation",
  bestPlatform: "Meilleure plateforme d'affiliation",
  comparison: "Comparatif des réseaux d'affiliation",
  cpaMonetization: "Monétisation d'audience au CPA",
  ecommerceAffiliation: "Affiliation pour e-commerçants"
} /* i18n:end */;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white" data-testid="site-footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png"
                alt="OptimHub"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg items-center justify-center">
                <span className="text-white font-bold text-xl">O</span>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed">
              {l("La plateforme DSP de nouvelle génération qui maximise vos performances publicitaires grâce à l'intelligence artificielle et des technologies de pointe.")}
            </p>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">{l('Services')}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((label) => (
                <li key={label}>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">{l('Entreprise')}</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('benefits')}
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {l('À propos')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {l('Témoignages')}
                </button>
              </li>
              <li>
                <a
                  href={routePath('publishers', currentLanguage)}
                  data-testid="footer-publishers-link"
                  className="text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {l('Accès API éditeurs')}
                </a>
              </li>
            </ul>
          </div>

          {/* Resources / SEO */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">{l('Ressources')}</h3>
            <ul className="space-y-3">
              {SEO_PAGE_KEYS.map((key) => (
                <li key={key}>
                  <a
                    href={routePath(key, currentLanguage)}
                    data-testid={`footer-seo-${key}`}
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {l(SEO_LINK_LABELS[key])}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">{l('Contact')}</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <span className="text-gray-300">contact@optimhub.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span className="text-gray-300">{l('Paris, France')}</span>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-500/20 rounded-lg p-4">
              <p className="text-sm text-blue-200">
                <strong>{l('Un accompagnement dédié')}</strong>
              </p>
              <p className="text-xs text-blue-300 mt-1">
                {l('Notre équipe vous accompagne pour maximiser votre ROI.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gray-800" />

      {/* Bottom Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <div className="text-gray-400 text-sm">
            © {currentYear} OptimHub. {l('Tous droits réservés.')}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a href={routePath('onePager', currentLanguage)} className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {l('One-Pager PDF')}
            </a>
            <a href={routePath('privacy', currentLanguage)} className="text-gray-400 hover:text-white transition-colors duration-200">
              {l('Politique de confidentialité')}
            </a>
            <a href={routePath('terms', currentLanguage)} className="text-gray-400 hover:text-white transition-colors duration-200">
              {l("Conditions d'utilisation")}
            </a>
            <a
              href={routePath('legalNotice', currentLanguage)}
              data-testid="footer-legal-notice-link"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              {LEGAL_NOTICE_LINKS[currentLanguage] || LEGAL_NOTICE_LINKS.en}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
